import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Combobox } from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, Info, UserPlus, Trash2, ShieldCheck, KeyRound, History, MonitorSmartphone, LogOut, Landmark, Database, Eraser, Copy, Plus, Cloud, RefreshCw, CloudUpload, Unplug, Archive, MessageSquare, Smartphone, Mail, Lock } from "lucide-react";
import { PasswordField } from "@/components/PasswordField";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { fmtDate } from "@/lib/format";
import ReminderStagesEditor from "@/components/ReminderStagesEditor";
import InboxSettings from "@/components/InboxSettings";
import { LETTER_TYPES } from "@/lib/format";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const NAV_MODULES = [
  { key: "contracts", label: "Contracten (maken, genereren & ondertekenen)" },
  { key: "outstanding", label: "Openstaand" },
  { key: "bulk-dunning", label: "Bulk-aanmaningen" },
  { key: "letter-generator", label: "Brievengenerator" },
  { key: "complaints", label: "Klachten" },
  { key: "payouts", label: "Uitbetalingen" },
  { key: "reports", label: "Rapportages" },
  { key: "tasks", label: "Taken" },
  { key: "import", label: "Importeren" },
  { key: "archive", label: "Archief" },
];
const TAB_MODULES = [
  { key: "tab-betalingen", label: "Betalingen (betaalgeschiedenis)" },
  { key: "tab-brieven", label: "Brieven" },
  { key: "tab-gesprekken", label: "Communicatie" },
  { key: "tab-factoring", label: "Factoring (facturen)" },
  { key: "tab-dossiers", label: "Dossiers" },
];
const KIND_MODULES = [
  { key: "type-huur", label: "Huurovereenkomsten" },
  { key: "type-lening", label: "Leningen" },
  { key: "tab-factoring", label: "Facturen (gekoppeld aan Factoring)" },
];
const BTN_MODULES = [
  { key: "btn-brievengenerator", label: "Knop: Brievengenerator" },
  { key: "btn-bericht", label: "Knop: Bericht (e-mail/SMS)" },
];

const SETTINGS_GROUPS = [
  ["Algemeen", [["gebruikers", "Gebruikers & toegang"], ["branding", "Branding"], ["modules", "Modules"], ["bank", "Bankrekeningen"]]],
  ["Debiteurenbeheer", [["catalogus", "Statussen & Profielen"], ["statuslabels", "Statuslabels"], ["herinneringen", "Herinneringen"], ["overeenkomst", "Overeenkomst"]]],
  ["Sjablonen", [["brieven", "Briefsjablonen"], ["berichten", "Berichtsjablonen"]]],
  ["Koppelingen", [["postvak", "Postvak"], ["sms", "SMS (Twilio)"], ["dropbox", "Dropbox back-up"]]],
  ["Overig", [["demo", "Demo & testdata"]]],
];

export default function Settings() {
  const [templates, setTemplates] = useState([]);
  const [templateClient, setTemplateClient] = useState("");
  const [tplClients, setTplClients] = useState([]);
  const [saving, setSaving] = useState("");
  const [users, setUsers] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwUser, setPwUser] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [newRole, setNewRole] = useState("user");
  const [historyUser, setHistoryUser] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [banks, setBanks] = useState([]);
  const [newIban, setNewIban] = useState("");
  const [newTag, setNewTag] = useState("");
  const [thresholds, setThresholds] = useState({ orange_days: 1, red_days: 14 });
  const [savingTh, setSavingTh] = useState(false);
  const [loanTpl, setLoanTpl] = useState("");
  const [savingTpl, setSavingTpl] = useState(false);
  const [stages, setStages] = useState([]);
  const [savingStages, setSavingStages] = useState(false);
  const [demoBusy, setDemoBusy] = useState("");
  const [catalog, setCatalog] = useState({ debtor_statuses: [], risk_profiles: [], action_types: [], message_templates: [] });
  const [newStat, setNewStat] = useState("");
  const [newAction, setNewAction] = useState("");
  const [newProf, setNewProf] = useState({ name: "", color: "amber" });
  const [hiddenMods, setHiddenMods] = useState([]);
  useEffect(() => { api.get("/catalog").then((r) => setHiddenMods(r.data.hidden_modules || [])).catch(() => {}); }, []);
  const toggleModule = (key) => {
    setHiddenMods((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      api.put("/catalog", { hidden_modules: next }).then(() => window.dispatchEvent(new Event("modules-changed"))).catch(() => toast.error("Opslaan mislukt"));
      return next;
    });
  };
  const [brand, setBrand] = useState({ brand_name: "", brand_color: "#0F1E3D", brand_logo: "", bcc_email: "" });
  useEffect(() => { api.get("/branding").then((r) => setBrand(r.data)).catch(() => {}); }, []);
  const saveBrand = async () => { try { await api.put("/branding", { brand_name: brand.brand_name, brand_color: brand.brand_color, bcc_email: brand.bcc_email }); toast.success("Instellingen opgeslagen"); } catch { toast.error("Opslaan mislukt"); } };
  const uploadLogo = async (e) => { const f = e.target.files?.[0]; if (!f) return; const fd = new FormData(); fd.append("file", f); try { const r = await api.post("/branding/logo", fd); setBrand((b) => ({ ...b, brand_logo: r.data.brand_logo })); toast.success("Logo geüpload"); } catch (er) { toast.error(er?.response?.data?.detail || "Upload mislukt"); } };
  const removeLogo = async () => { await api.delete("/branding/logo"); setBrand((b) => ({ ...b, brand_logo: "" })); };

  // Dropbox integratie
  const [dbx, setDbx] = useState({ configured: false });
  const [dbxForm, setDbxForm] = useState({ app_key: "", app_secret: "", refresh_token: "" });
  const [dbxBusy, setDbxBusy] = useState("");
  const [backups, setBackups] = useState([]);
  // Twilio SMS
  const [twilio, setTwilio] = useState({ configured: false });
  const [twForm, setTwForm] = useState({ account_sid: "", auth_token: "", phone_number: "" });
  const [twBusy, setTwBusy] = useState(false);
  const loadTwilio = () => api.get("/twilio/settings").then((r) => setTwilio(r.data)).catch(() => {});
  useEffect(() => { loadTwilio(); }, []);
  const saveTwilio = async () => {
    setTwBusy(true);
    try {
      const r = await api.post("/twilio/settings", twForm);
      toast.success(`Twilio gekoppeld${r.data.friendly_name ? `: ${r.data.friendly_name}` : ""}`);
      setTwForm({ account_sid: "", auth_token: "", phone_number: "" });
      await loadTwilio();
    } catch (e) { toast.error(e?.response?.data?.detail || "Koppelen mislukt"); }
    finally { setTwBusy(false); }
  };
  const disconnectTwilio = async () => {
    try { await api.delete("/twilio/settings"); toast.success("Twilio ontkoppeld"); await loadTwilio(); }
    catch { toast.error("Ontkoppelen mislukt"); }
  };
  const loadDropbox = () => api.get("/dropbox/settings").then((r) => setDbx(r.data)).catch(() => {});
  const loadBackups = () => api.get("/dropbox/backups").then((r) => setBackups(r.data)).catch(() => {});
  useEffect(() => { loadDropbox(); loadBackups(); }, []);
  // Ververs historie zolang er een back-up loopt
  useEffect(() => {
    if (!backups.some((b) => b.status === "running")) return;
    const t = setInterval(() => { loadBackups(); loadDropbox(); }, 3000);
    return () => clearInterval(t);
  }, [backups]);
  const saveDropbox = async () => {
    setDbxBusy("save");
    try {
      const r = await api.post("/dropbox/settings", dbxForm);
      toast.success(`Dropbox gekoppeld: ${r.data.account_name || r.data.account_email || "verbonden"}`);
      setDbxForm({ app_key: "", app_secret: "", refresh_token: "" });
      await loadDropbox();
    } catch (e) { toast.error(e?.response?.data?.detail || "Koppelen mislukt"); }
    setDbxBusy("");
  };
  const testDropbox = async () => {
    setDbxBusy("test");
    try { const r = await api.post("/dropbox/test"); toast.success(`Verbinding OK: ${r.data.account_name || r.data.account_email}`); await loadDropbox(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Verbinding mislukt"); }
    setDbxBusy("");
  };
  const disconnectDropbox = async () => {
    setDbxBusy("disc");
    try { await api.delete("/dropbox/settings"); toast.success("Dropbox-koppeling verwijderd"); await loadDropbox(); }
    catch { toast.error("Verwijderen mislukt"); }
    setDbxBusy("");
  };
  const backupNow = async () => {
    setDbxBusy("backup");
    try { const r = await api.post("/dropbox/backup"); toast.success(r.data?.message || "Back-up gestart"); await loadBackups(); await loadDropbox(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Back-up mislukt"); }
    setDbxBusy("");
  };

  useEffect(() => { api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {}); }, []);
  const saveCatalog = async (next) => {
    setCatalog(next);
    try { await api.put("/catalog", next); toast.success("Catalogus opgeslagen"); }
    catch { toast.error("Opslaan mislukt"); }
  };
  const addStat = () => { const s = newStat.trim(); if (s && !catalog.debtor_statuses.includes(s)) saveCatalog({ ...catalog, debtor_statuses: [...catalog.debtor_statuses, s] }); setNewStat(""); };
  const removeStat = (s) => saveCatalog({ ...catalog, debtor_statuses: catalog.debtor_statuses.filter((x) => x !== s) });
  const addAction = () => { const a = newAction.trim(); if (a && !(catalog.action_types || []).includes(a)) saveCatalog({ ...catalog, action_types: [...(catalog.action_types || []), a] }); setNewAction(""); };
  const removeAction = (a) => saveCatalog({ ...catalog, action_types: (catalog.action_types || []).filter((x) => x !== a) });
  const addProf = () => { const n = newProf.name.trim(); if (n && !catalog.risk_profiles.some((p) => p.name === n)) saveCatalog({ ...catalog, risk_profiles: [...catalog.risk_profiles, { name: n, color: newProf.color }] }); setNewProf({ name: "", color: "amber" }); };
  const removeProf = (n) => saveCatalog({ ...catalog, risk_profiles: catalog.risk_profiles.filter((p) => p.name !== n) });
  const setTpl = (i, k, v) => setCatalog((c) => ({ ...c, message_templates: (c.message_templates || []).map((t, idx) => idx === i ? { ...t, [k]: v } : t) }));
  const addTpl = () => setCatalog((c) => ({ ...c, message_templates: [...(c.message_templates || []), { label: "Nieuw sjabloon", subject: "", body: "" }] }));
  const removeTpl = (i) => setCatalog((c) => ({ ...c, message_templates: (c.message_templates || []).filter((_, idx) => idx !== i) }));
  const saveTpls = () => saveCatalog(catalog);

  const copyFromGeneral = async () => {
    if (!templateClient) return;
    setSaving("copy");
    try {
      const gen = (await api.get("/templates")).data;
      for (const t of gen) {
        await api.put(`/templates/${t.type}?client_id=${templateClient}`, { type: t.type, client_id: templateClient, subject: t.subject, body: t.body, tone: t.tone, variants: t.variants || [] });
      }
      toast.success("Algemene set gekopieerd naar deze opdrachtgever");
      load(templateClient);
    } catch { toast.error("Kopiëren mislukt"); }
    finally { setSaving(""); }
  };

  const seedDemo = async () => {
    setDemoBusy("seed");
    try {
      const r = await api.post("/admin/seed-demo");
      const c = r.data?.counts || {};
      toast.success(`Testdata gevuld: ${c.tenants || 0} debiteuren, ${c.contracts || 0} contracten, ${c.payments || 0} termijnen.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Vullen mislukt"); }
    finally { setDemoBusy(""); }
  };
  const wipeDemo = async () => {
    setDemoBusy("wipe");
    try { await api.post("/admin/wipe-demo"); toast.success("Alle debiteurendata is gewist."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Wissen mislukt"); }
    finally { setDemoBusy(""); }
  };

  const load = (cid = templateClient) => api.get(`/templates${cid ? `?client_id=${cid}` : ""}`).then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => { api.get("/clients").then((r) => setTplClients(r.data)).catch(() => {}); }, []);
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "gebruikers");
  useEffect(() => {
    const qTab = searchParams.get("tab"); const qClient = searchParams.get("client");
    if (qTab) setTab(qTab);
    if (qClient) { setTemplateClient(qClient); load(qClient); }
    // eslint-disable-next-line
  }, [searchParams]);
  const loadUsers = () => api.get("/backend-auth/users").then((r) => setUsers(r.data)).catch(() => {});
  const loadSessions = () => api.get("/backend-auth/sessions").then((r) => setSessions(r.data)).catch(() => {});
  const loadBanks = () => api.get("/bank-accounts").then((r) => setBanks(r.data)).catch(() => {});
  const loadTh = () => api.get("/settings/status-thresholds").then((r) => setThresholds(r.data)).catch(() => {});
  const loadLoanTpl = () => api.get("/settings/loan-template").then((r) => setLoanTpl(r.data.template)).catch(() => {});
  const loadStages = () => api.get("/settings/reminder-stages").then((r) => setStages(r.data.stages || [])).catch(() => {});
  useEffect(() => { load(); loadUsers(); loadSessions(); loadBanks(); loadTh(); loadLoanTpl(); loadStages(); }, []);

  const saveStages = async () => {
    setSavingStages(true);
    try {
      const payload = stages.map((s) => ({ ...s, offset: parseInt(s.offset, 10) || 0 }));
      const r = await api.put("/settings/reminder-stages", { stages: payload });
      setStages(r.data.stages || payload);
      toast.success("Herinnerings-momenten opgeslagen");
    } catch { toast.error("Opslaan mislukt"); }
    finally { setSavingStages(false); }
  };

  const saveLoanTpl = async () => {
    setSavingTpl(true);
    try { await api.put("/settings/loan-template", { template: loanTpl }); toast.success("Overeenkomst-sjabloon opgeslagen"); }
    catch { toast.error("Opslaan mislukt"); }
    finally { setSavingTpl(false); }
  };

  const saveThresholds = async () => {
    setSavingTh(true);
    try { await api.put("/settings/status-thresholds", { orange_days: Number(thresholds.orange_days) || 0, red_days: Number(thresholds.red_days) || 0 }); toast.success("Statuslabels opgeslagen"); }
    catch { toast.error("Opslaan mislukt"); }
    finally { setSavingTh(false); }
  };

  const sendWelcome = async (email) => {
    try { await api.post(`/backend-auth/users/${encodeURIComponent(email)}/welcome`); toast.success(`Welkomstmail verstuurd naar ${email}`); loadUsers(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Versturen mislukt"); }
  };
  const addUser = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newEmail.trim())) { toast.error("Vul eerst een geldig e-mailadres in"); return; }
    if (newPw && newPw.length < 8) { toast.error("Wachtwoord minimaal 8 tekens"); return; }
    try { const r = await api.post("/backend-auth/users", { email: newEmail.trim(), role: newRole, password: newPw || null }); toast.success(r.data.welcome_sent ? "Gebruiker aangemaakt — welkomstmail verstuurd" : "Gebruiker aangemaakt (welkomstmail kon niet worden verstuurd)"); setNewEmail(""); setNewPw(""); loadUsers(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Aanmaken mislukt"); }
  };
  const savePw = async () => {
    if (pwValue.length < 8) { toast.error("Wachtwoord minimaal 8 tekens"); return; }
    try { await api.post(`/backend-auth/users/${encodeURIComponent(pwUser)}/password`, { password: pwValue }); toast.success(`Wachtwoord ingesteld voor ${pwUser}`); setPwUser(null); setPwValue(""); loadUsers(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Instellen mislukt"); }
  };
  const delUser = async (email) => {
    if (!window.confirm(`Gebruiker ${email} verwijderen? Deze kan dan niet meer inloggen.`)) return;
    try { await api.delete(`/backend-auth/users/${encodeURIComponent(email)}`); toast.success("Verwijderd"); loadUsers(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Verwijderen mislukt"); }
  };
  const resetUser = async (email) => {
    try { await api.post(`/backend-auth/users/${encodeURIComponent(email)}/reset`); toast.success("Wachtwoord en 2FA gereset. Gebruiker stelt deze bij volgende login opnieuw in."); loadUsers(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reset mislukt"); }
  };
  const openHistory = async (email) => {
    setHistoryUser(email); setHistory([]); setHistoryLoading(true);
    try { const r = await api.get(`/backend-auth/users/${encodeURIComponent(email)}/history`); setHistory(r.data); }
    catch { toast.error("Historie ophalen mislukt"); }
    finally { setHistoryLoading(false); }
  };
  const revokeSession = async (token) => {
    try { await api.delete(`/backend-auth/sessions/${encodeURIComponent(token)}`); toast.success("Sessie beëindigd"); loadSessions(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Beëindigen mislukt"); }
  };

  const addBank = async () => {
    if (!newIban.trim()) return toast.error("Vul een IBAN in");
    try { await api.post("/bank-accounts", { iban: newIban.trim(), name_tag: newTag.trim() }); toast.success("Rekening toegevoegd"); setNewIban(""); setNewTag(""); loadBanks(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Toevoegen mislukt"); }
  };
  const delBank = async (bid) => {
    try { await api.delete(`/bank-accounts/${bid}`); toast.success("Rekening verwijderd"); loadBanks(); }
    catch { toast.error("Verwijderen mislukt"); }
  };

  const update = (type, k, v) => setTemplates((ts) => ts.map((t) => t.type === type ? { ...t, [k]: v } : t));
  const updateVariant = (type, idx, k, v) => { setTemplates((ts) => ts.map((t) => t.type === type ? { ...t, variants: (t.variants || []).map((vr, i) => i === idx ? { ...vr, [k]: v } : vr) } : t)); autosave(type); };

  const saveTimers = useRef({});
  const templatesRef = useRef([]);
  useEffect(() => { templatesRef.current = templates; }, [templates]);
  const [savedType, setSavedType] = useState("");
  const autosave = (type) => {
    clearTimeout(saveTimers.current[type]);
    saveTimers.current[type] = setTimeout(() => {
      const tpl = templatesRef.current.find((x) => x.type === type);
      if (tpl) save(tpl, true);
    }, 900);
  };

  const save = async (t, silent = false) => {
    setSaving(t.type);
    const variants = t.variants || [];
    const primary = variants[0] || { subject: t.subject, body: t.body, tone: t.tone };
    try {
      await api.put(`/templates/${t.type}${templateClient ? `?client_id=${templateClient}` : ""}`, { type: t.type, client_id: templateClient, subject: primary.subject, body: primary.body, tone: primary.tone, variants });
      if (silent) { setSavedType(t.type); setTimeout(() => setSavedType((s) => s === t.type ? "" : s), 2000); }
      else toast.success(templateClient ? "Sjabloon opgeslagen voor opdrachtgever" : "Sjabloon opgeslagen");
    } catch { toast.error("Automatisch opslaan mislukt"); }
    finally { setSaving(""); }
  };

  return (
    <div className="space-y-6 min-w-0 max-w-full">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Instellingen</h1>
        <p className="text-slate-500">Beheer gebruikers, bankrekeningen en de standaard briefsjablonen.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList data-testid="settings-tabs" className="h-auto w-full flex flex-col items-stretch gap-2 p-3 bg-slate-100">
          {SETTINGS_GROUPS.map(([group, items]) => (
            <div key={group} className="flex flex-wrap items-center gap-1">
              <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 w-full sm:w-40 shrink-0">{group}</span>
              {items.map(([v, l]) => (
                <TabsTrigger key={v} value={v} data-testid={`settings-tab-${v}`} className="data-[state=active]:bg-white">{l}</TabsTrigger>
              ))}
            </div>
          ))}
        </TabsList>

        <TabsContent value="gebruikers" className="space-y-6 mt-4">
      <Card className="p-6">
        <h2 className="font-heading text-lg font-semibold text-slate-800 flex items-center gap-2 mb-4"><ShieldCheck className="h-5 w-5" /> Gebruikers (login met 2FA)</h2>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <Input type="email" placeholder="e-mailadres nieuwe gebruiker" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addUser(); }} data-testid="new-user-email" />
          <PasswordField value={newPw} onChange={setNewPw} placeholder="wachtwoord (optioneel)" testId="new-user-password" />
          <div className="sm:w-40"><Combobox testId="new-user-role" value={newRole} onChange={setNewRole} options={[{ value: "user", label: "Gebruiker" }, { value: "admin", label: "Beheerder" }]} /></div>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={addUser} data-testid="add-user-button"><UserPlus className="h-4 w-4 mr-2" /> Toevoegen</Button>
        </div>
        <p className="text-xs text-slate-400 mb-3">Nieuwe gebruikers krijgen automatisch een welkomstmail met inloglink en uitleg (zonder wachtwoord). Vul je een wachtwoord in, geef dat dan zelf door; anders kiest de gebruiker bij de eerste login zelf een wachtwoord. 2FA koppelt de gebruiker bij de eerste login. Sessies verlopen na 4 uur.</p>
        <div className="space-y-2">
          {users.map((u) => (
            <div key={u.email} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg border" data-testid={`user-row-${u.email}`}>
              <div className="min-w-0">
                <div className="font-medium text-slate-800 flex flex-wrap items-center gap-2 break-all">{u.email} <Badge variant="secondary" className="capitalize">{u.role}</Badge>{u.superadmin && <Badge className="bg-[#0F1E3D] text-[#C9A227]">superadmin</Badge>}</div>
                <div className="text-xs text-slate-500">Laatst ingelogd: {u.last_login ? fmtDate(u.last_login) : "nog nooit"} • {u.totp_enabled ? "2FA actief" : (u.must_set_password ? "wacht op eerste login" : "2FA in te stellen")}{u.welcome_sent_at ? ` • welkomstmail ${fmtDate(u.welcome_sent_at)}` : ""}</div>
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                <Button size="icon" variant="ghost" className="h-8 w-8 text-blue-600" onClick={() => sendWelcome(u.email)} title="Welkomstmail (opnieuw) versturen" data-testid={`welcome-user-${u.email}`}><Mail className="h-4 w-4" /></Button>
                {!u.superadmin && <Button size="icon" variant="ghost" className="h-8 w-8 text-[#0F1E3D]" onClick={() => { setPwUser(u.email); setPwValue(""); }} title="Wachtwoord instellen" data-testid={`setpw-user-${u.email}`}><Lock className="h-4 w-4" /></Button>}
                <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500" onClick={() => openHistory(u.email)} title="Login-historie" data-testid={`history-user-${u.email}`}><History className="h-4 w-4" /></Button>
                {!u.superadmin && <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-amber-600" title="Wachtwoord/2FA resetten" data-testid={`reset-user-${u.email}`}><KeyRound className="h-4 w-4" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid={`reset-dialog-${u.email}`}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Wachtwoord en 2FA resetten?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Hiermee worden het wachtwoord en de 2FA van <strong>{u.email}</strong> gewist. De gebruiker moet bij de volgende login opnieuw een wachtwoord instellen en 2FA activeren.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="reset-cancel">Annuleren</AlertDialogCancel>
                      <AlertDialogAction className="bg-amber-600 hover:bg-amber-700" onClick={() => resetUser(u.email)} data-testid={`reset-confirm-${u.email}`}>Ja, resetten</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>}
                {!u.superadmin && <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => delUser(u.email)} data-testid={`delete-user-${u.email}`}><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="font-heading text-lg font-semibold text-slate-800 flex items-center gap-2 mb-4"><MonitorSmartphone className="h-5 w-5" /> Actieve sessies</h2>
        <p className="text-xs text-slate-400 mb-3">Overzicht van gebruikers die op dit moment zijn ingelogd. Beëindig een sessie om die op afstand uit te loggen.</p>
        <div className="space-y-2" data-testid="sessions-list">
          {sessions.length === 0 && <p className="text-sm text-slate-500">Geen actieve sessies.</p>}
          {sessions.map((s) => (
            <div key={s.token} className="flex items-center justify-between gap-2 p-3 rounded-lg border" data-testid={`session-row-${s.token}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-800 break-all">{s.email}</div>
                <div className="text-xs text-slate-500">Ingelogd: {s.created_at ? fmtDate(s.created_at) : "—"} • IP: {s.ip || "onbekend"}</div>
                <div className="text-xs text-slate-400 truncate">{s.device || "onbekend apparaat"}</div>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="ghost" className="text-red-500" title="Sessie beëindigen" data-testid={`revoke-session-${s.token}`}><LogOut className="h-4 w-4 mr-1" /> Beëindigen</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Sessie beëindigen?</AlertDialogTitle>
                    <AlertDialogDescription>De sessie van <strong>{s.email}</strong> wordt direct beëindigd. De gebruiker moet opnieuw inloggen.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuleren</AlertDialogCancel>
                    <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => revokeSession(s.token)} data-testid={`revoke-confirm-${s.token}`}>Ja, beëindigen</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={!!pwUser} onOpenChange={(o) => { if (!o) setPwUser(null); }}>
        <DialogContent className="max-w-md" data-testid="setpw-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Lock className="h-5 w-5 text-[#C9A227]" /> Wachtwoord instellen</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Nieuw wachtwoord voor <strong>{pwUser}</strong> (minimaal 8 tekens). Het wordt niet gemaild; geef het zelf door. Heeft de gebruiker nog geen 2FA, dan koppelt hij die bij de eerste login.</p>
          <PasswordField value={pwValue} onChange={setPwValue} placeholder="nieuw wachtwoord" testId="setpw-input" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setPwUser(null)} data-testid="setpw-cancel">Annuleren</Button>
            <Button className="bg-[#0F1E3D] hover:bg-[#1B2D52]" onClick={savePw} disabled={pwValue.length < 8} data-testid="setpw-save">Opslaan</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!historyUser} onOpenChange={(o) => !o && setHistoryUser(null)}>
        <DialogContent data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5" /> Login-historie — {historyUser}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {historyLoading && <p className="text-sm text-slate-500">Laden...</p>}
            {!historyLoading && history.length === 0 && <p className="text-sm text-slate-500">Nog geen inlogmomenten geregistreerd.</p>}
            {history.map((h, i) => (
              <div key={i} className="p-3 rounded-lg border text-sm" data-testid={`history-entry-${i}`}>
                <div className="font-medium text-slate-800">{fmtDate(h.at)}</div>
                <div className="text-xs text-slate-500">IP: {h.ip || "onbekend"}</div>
                <div className="text-xs text-slate-400 break-all">{h.device || "onbekend apparaat"}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
        </TabsContent>

        <TabsContent value="bank" className="space-y-6 mt-4">
          <Card className="p-6">
            <h2 className="font-heading text-lg font-semibold text-slate-800 flex items-center gap-2 mb-4"><Landmark className="h-5 w-5" /> Bankrekeningen</h2>
            <p className="text-xs text-slate-400 mb-3">Rekeningen waar debiteuren naartoe betalen. Per overeenkomst kies je welke rekening geldt; deze wordt gebruikt in de herinneringen ({"{iban}"}).</p>
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <Input placeholder="IBAN (bijv. NL00 BANK 0000 0000 00)" value={newIban} onChange={(e) => setNewIban(e.target.value)} data-testid="new-bank-iban" />
              <Input placeholder="Naamtag (bijv. Hoofdrekening)" value={newTag} onChange={(e) => setNewTag(e.target.value)} data-testid="new-bank-tag" />
              <Button className="bg-emerald-600 hover:bg-emerald-700 shrink-0" onClick={addBank} disabled={!newIban.trim()} data-testid="add-bank-button"><UserPlus className="h-4 w-4 mr-2" /> Toevoegen</Button>
            </div>
            <div className="space-y-2" data-testid="bank-accounts-list">
              {banks.length === 0 && <p className="text-sm text-slate-500">Nog geen bankrekeningen toegevoegd.</p>}
              {banks.map((b) => (
                <div key={b.id} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`bank-row-${b.id}`}>
                  <div>
                    <div className="font-medium text-slate-800">{b.name_tag || "Zonder naamtag"}</div>
                    <div className="text-xs text-slate-500 font-mono">{b.iban}</div>
                  </div>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => delBank(b.id)} data-testid={`delete-bank-${b.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="statuslabels" className="space-y-6 mt-4">
          <Card className="p-6 space-y-4">
            <h2 className="font-heading text-lg font-semibold text-slate-800">Statuslabels per debiteur</h2>
            <p className="text-sm text-slate-500">Bepaal wanneer een debiteur een oranje of rood label krijgt, gemeten in dagen ná de vervaldatum van de oudste openstaande termijn.</p>
            <div className="flex items-center gap-3">
              <span className="inline-block h-3 w-3 rounded-full bg-amber-500" />
              <Label className="w-40">Oranje vanaf (dagen)</Label>
              <Input type="number" className="w-32" value={thresholds.orange_days} onChange={(e) => setThresholds({ ...thresholds, orange_days: e.target.value })} data-testid="threshold-orange-input" />
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-block h-3 w-3 rounded-full bg-red-500" />
              <Label className="w-40">Rood vanaf (dagen)</Label>
              <Input type="number" className="w-32" value={thresholds.red_days} onChange={(e) => setThresholds({ ...thresholds, red_days: e.target.value })} data-testid="threshold-red-input" />
            </div>
            <p className="text-xs text-slate-400">Groen = geen achterstand (of binnen de oranje-drempel). Wijzigingen gelden direct voor alle overzichten.</p>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={saveThresholds} disabled={savingTh} data-testid="threshold-save-button">
              <Save className="h-4 w-4 mr-2" /> {savingTh ? "Opslaan..." : "Opslaan"}
            </Button>
          </Card>
        </TabsContent>

        <TabsContent value="overeenkomst" className="space-y-6 mt-4">
          <Card className="p-6 space-y-3">
            <h2 className="font-heading text-lg font-semibold text-slate-800">Standaard leningsovereenkomst</h2>
            <p className="text-sm text-slate-500">Deze tekst wordt gebruikt bij het opstellen van een leningsovereenkomst. Beschikbare velden: <code className="text-xs">{"{debiteur} {adres} {bedrag} {looptijd} {rente} {maandbedrag} {iban} {betaaloverzicht} {datum}"}</code></p>
            <Textarea rows={16} value={loanTpl} onChange={(e) => setLoanTpl(e.target.value)} className="font-mono text-xs" data-testid="loan-template-input" />
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={saveLoanTpl} disabled={savingTpl} data-testid="loan-template-save">
              <Save className="h-4 w-4 mr-2" /> {savingTpl ? "Opslaan..." : "Sjabloon opslaan"}
            </Button>
          </Card>
          <Card className="p-6 space-y-4" data-testid="sign-reminder-card">
            <h2 className="font-heading text-lg font-semibold text-slate-800">Herinnering bij niet tekenen</h2>
            <p className="text-sm text-slate-500">Ondertekenaars die nog niet hebben getekend krijgen automatisch een herinnering met hun eigen ondertekenlink. De controle draait elke ochtend om 9:00. Elke herinnering komt in het logboek en de tijdslijn.</p>
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="font-medium text-sm text-slate-800">Automatische herinneringen versturen</div>
              <Switch checked={catalog.sign_reminder_enabled !== false} onCheckedChange={(v) => saveCatalog({ ...catalog, sign_reminder_enabled: v })} data-testid="sign-reminder-toggle" />
            </div>
            {catalog.sign_reminder_enabled !== false && (
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-40"><Label className="text-xs text-slate-500">Na / elke … dagen</Label><Input type="number" min={1} value={catalog.sign_reminder_days ?? 3} onChange={(e) => setCatalog((c) => ({ ...c, sign_reminder_days: e.target.value }))} data-testid="sign-reminder-days" /></div>
                <div className="w-40"><Label className="text-xs text-slate-500">Maximaal aantal</Label><Input type="number" min={1} max={10} value={catalog.sign_reminder_max ?? 3} onChange={(e) => setCatalog((c) => ({ ...c, sign_reminder_max: e.target.value }))} data-testid="sign-reminder-max" /></div>
                <Button variant="outline" onClick={() => saveCatalog({ ...catalog, sign_reminder_days: Math.max(1, parseInt(catalog.sign_reminder_days) || 3), sign_reminder_max: Math.min(10, Math.max(1, parseInt(catalog.sign_reminder_max) || 3)) })} data-testid="sign-reminder-save"><Save className="h-4 w-4 mr-2" /> Opslaan</Button>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="herinneringen" className="space-y-6 mt-4">
          <Card className="p-6 space-y-4" data-testid="reminder-stages-card">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-heading text-lg font-semibold text-slate-800">Automatische herinneringen</h2>
                <p className="text-sm text-slate-500 max-w-2xl">Bepaal wanneer een herinnering of aanmaning automatisch wordt verstuurd t.o.v. de vervaldatum. Negatief = vóór verval, positief = ná verval. Per debiteur wordt automatisch verzonden als 'auto-aanmaning' aanstaat; anders komt de brief als concept klaar. Velden: <code className="text-xs">{"{huurder} {maand} {bedrag} {iban} {boete_per_dag}"}</code></p>
              </div>
            </div>
            <ReminderStagesEditor stages={stages} setStages={setStages} />
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={saveStages} disabled={savingStages} data-testid="reminder-stages-save">
              <Save className="h-4 w-4 mr-2" /> {savingStages ? "Opslaan..." : "Herinneringen opslaan"}
            </Button>
          </Card>
        </TabsContent>

        <TabsContent value="brieven" className="space-y-6 mt-4">

      <Card className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="template-client-selector">
        <span className="text-sm font-medium text-slate-700">Sjabloonset voor:</span>
        <div className="w-full sm:w-80"><Combobox testId="template-client-select" value={templateClient || "all"} onChange={(v) => { const cid = v === "all" ? "" : v; setTemplateClient(cid); load(cid); }} searchPlaceholder="Zoek opdrachtgever…" options={[{ value: "all", label: "Algemeen (standaard voor alle opdrachtgevers)" }, ...tplClients.map((c) => ({ value: c.id, label: c.name }))]} /></div>
        <span className="text-xs text-slate-400">{templateClient ? "Alleen deze opdrachtgever gebruikt deze set; leeg gelaten velden vallen terug op Algemeen." : "Deze set geldt voor iedereen zonder eigen set."}</span>
        {templateClient && <Button variant="outline" size="sm" onClick={copyFromGeneral} disabled={saving === "copy"} data-testid="template-copy-general" className="sm:ml-auto"><Copy className="h-4 w-4 mr-1.5" /> {saving === "copy" ? "Bezig…" : "Kopieer vanaf Algemeen"}</Button>}
      </Card>

      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
        <Info className="h-5 w-5 shrink-0 mt-0.5" />
        <div>Gebruik variabelen zoals <code className="font-mono">{"{huurder}"}</code>, <code className="font-mono">{"{maand}"}</code>, <code className="font-mono">{"{bedrag}"}</code>, <code className="font-mono">{"{iban}"}</code> en <code className="font-mono">{"{boete_per_dag}"}</code>. Bij het genereren met AI (Claude Sonnet 4.6) worden deze automatisch ingevuld met de echte gegevens van de huurder.</div>
      </div>

      <Tabs defaultValue="herinnering_1">
        <TabsList className="h-auto flex-wrap justify-start">
          {templates.map((t) => <TabsTrigger key={t.type} value={t.type} data-testid={`template-tab-${t.type}`}>{LETTER_TYPES[t.type]}</TabsTrigger>)}
        </TabsList>
        {templates.map((t) => (
          <TabsContent key={t.type} value={t.type} className="mt-4">
            <Card className="p-6 space-y-4">
              <p className="text-sm text-slate-500">Sla per toon een variant op. Bij het versturen kies je AI of de kant-en-klare variant met deze toon.</p>
              {(t.variants || []).map((vr, i) => (
                <div key={i} className="border rounded-lg p-4 space-y-3" data-testid={`template-variant-${t.type}-${i}`}>
                  <div className="flex items-center gap-2"><Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">{vr.tone}</Badge></div>
                  <div><Label>Onderwerp</Label><Input value={vr.subject} onChange={(e) => updateVariant(t.type, i, "subject", e.target.value)} data-testid={`template-variant-subject-${t.type}-${i}`} /></div>
                  <div><Label>Sjabloontekst</Label><Textarea rows={8} value={vr.body} onChange={(e) => updateVariant(t.type, i, "body", e.target.value)} className="font-mono text-xs" data-testid={`template-variant-body-${t.type}-${i}`} /></div>
                </div>
              ))}
              <div className="flex items-center gap-2 text-sm" data-testid={`template-autosave-${t.type}`}>
                {saving === t.type
                  ? <span className="text-slate-400 flex items-center gap-1"><Save className="h-4 w-4 animate-pulse" /> Automatisch opslaan…</span>
                  : savedType === t.type
                    ? <span className="text-emerald-600 flex items-center gap-1"><Save className="h-4 w-4" /> Opgeslagen ✓</span>
                    : <span className="text-slate-400 flex items-center gap-1"><Save className="h-4 w-4" /> Wijzigingen worden automatisch opgeslagen</span>}
              </div>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
        </TabsContent>

        <TabsContent value="berichten" className="space-y-6 mt-4">
          <Card className="p-6 space-y-4" data-testid="message-templates-card">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading font-semibold">Berichtsjablonen (WhatsApp / SMS / e-mail)</h3>
                <p className="text-sm text-slate-500">Standaardteksten voor de Bericht-wizard. Gebruik <code className="bg-slate-100 px-1 rounded">{"{naam}"}</code> en <code className="bg-slate-100 px-1 rounded">{"{bedrag}"}</code> als plaatshouders.</p>
              </div>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={saveTpls} disabled={saving === "catalog"} data-testid="message-templates-save"><Save className="h-4 w-4 mr-2" /> Opslaan</Button>
            </div>
            <div className="space-y-4">
              {(catalog.message_templates || []).map((t, i) => (
                <div key={i} className="border rounded-lg p-4 space-y-2" data-testid={`message-template-${i}`}>
                  <div className="flex items-center gap-2">
                    <Input value={t.label || ""} onChange={(e) => setTpl(i, "label", e.target.value)} placeholder="Naam van sjabloon" className="font-medium" data-testid={`message-template-${i}-label`} />
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 shrink-0" onClick={() => removeTpl(i)} data-testid={`message-template-${i}-remove`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <Input value={t.subject || ""} onChange={(e) => setTpl(i, "subject", e.target.value)} placeholder="Onderwerp (voor e-mail)" data-testid={`message-template-${i}-subject`} />
                  <Textarea rows={4} value={t.body || ""} onChange={(e) => setTpl(i, "body", e.target.value)} placeholder="Berichttekst…" data-testid={`message-template-${i}-body`} />
                </div>
              ))}
              {(catalog.message_templates || []).length === 0 && <p className="text-sm text-slate-400">Nog geen berichtsjablonen.</p>}
              <Button variant="outline" onClick={addTpl} data-testid="message-template-add"><Plus className="h-4 w-4 mr-1" /> Sjabloon toevoegen</Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="demo" className="space-y-6 mt-4">
          <Card className="p-6 space-y-4" data-testid="demo-data-card">
            <h2 className="font-heading text-lg font-semibold text-slate-800 flex items-center gap-2"><Database className="h-5 w-5" /> Demo &amp; testdata</h2>
            <p className="text-sm text-slate-500 max-w-2xl">Vul de omgeving met één klik met ruime, realistische voorbeelddata (opdrachtgevers, panden, debiteuren, contracten, betalingen, belnotities en factoring). Handig om alle functies te bekijken of een demo te geven. De demo-debiteur krijgt <strong>uw eigen e-mailadres</strong>, zodat u het debiteurenportaal en iDEAL kunt testen via <code className="text-xs">/portaal</code>.</p>
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              <Info className="h-5 w-5 shrink-0 mt-0.5" />
              <div>Beide acties <strong>wissen eerst al uw bestaande debiteurendata</strong> (opdrachtgevers, panden, debiteuren, contracten, betalingen, notities, factoring en bankrekeningen). Dit kan niet ongedaan worden gemaakt.</div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={!!demoBusy} data-testid="seed-demo-button"><Database className="h-4 w-4 mr-2" /> {demoBusy === "seed" ? "Bezig met vullen…" : "Testdata (opnieuw) vullen"}</Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="seed-demo-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Testdata vullen?</AlertDialogTitle>
                    <AlertDialogDescription>Hiermee wordt <strong>alle bestaande debiteurendata gewist</strong> en vervangen door verse voorbeelddata. Doorgaan?</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="seed-demo-cancel">Annuleren</AlertDialogCancel>
                    <AlertDialogAction className="bg-emerald-600 hover:bg-emerald-700" onClick={seedDemo} data-testid="seed-demo-confirm">Ja, vullen</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" disabled={!!demoBusy} data-testid="wipe-demo-button"><Eraser className="h-4 w-4 mr-2" /> {demoBusy === "wipe" ? "Bezig met wissen…" : "Alle data wissen"}</Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="wipe-demo-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Alle data wissen?</AlertDialogTitle>
                    <AlertDialogDescription>Hiermee wordt <strong>alle debiteurendata definitief verwijderd</strong>. Dit kan niet ongedaan worden gemaakt. Doorgaan?</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="wipe-demo-cancel">Annuleren</AlertDialogCancel>
                    <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={wipeDemo} data-testid="wipe-demo-confirm">Ja, alles wissen</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
            <p className="text-xs text-slate-400">Na het vullen ververst u de andere pagina's (Dashboard, Debiteuren) om de nieuwe data te zien.</p>
          </Card>
        </TabsContent>

        <TabsContent value="catalogus" className="space-y-6 mt-4">
          <Card className="p-6 space-y-4" data-testid="auto-archive-card">
            <div className="flex items-center gap-2">
              <Archive className="h-5 w-5 text-slate-600" />
              <h2 className="font-heading text-lg font-semibold text-slate-800">Automatisch archiveren</h2>
            </div>
            <p className="text-sm text-slate-500">Verplaats afgehandelde leningen en facturen automatisch naar het archief zodra ze een bepaald aantal dagen zijn afgehandeld. De controle draait dagelijks. Handmatig gearchiveerde items blijven ongewijzigd.</p>
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <div className="font-medium text-sm text-slate-800">Automatisch archiveren inschakelen</div>
                <div className="text-xs text-slate-400">Tellen vanaf het moment dat het item is afgehandeld.</div>
              </div>
              <Switch checked={!!catalog.auto_archive_enabled} onCheckedChange={(v) => saveCatalog({ ...catalog, auto_archive_enabled: v })} data-testid="auto-archive-toggle" />
            </div>
            {catalog.auto_archive_enabled && (
              <div className="flex items-end gap-3">
                <div className="w-40">
                  <Label className="text-xs text-slate-500">Aantal dagen</Label>
                  <Input type="number" min={1} value={catalog.auto_archive_days ?? 90} onChange={(e) => setCatalog((c) => ({ ...c, auto_archive_days: e.target.value }))} data-testid="auto-archive-days" />
                </div>
                <Button variant="outline" onClick={() => saveCatalog({ ...catalog, auto_archive_days: Math.max(1, parseInt(catalog.auto_archive_days) || 90) })} data-testid="auto-archive-save"><Save className="h-4 w-4 mr-2" /> Opslaan</Button>
                <span className="text-xs text-slate-400 pb-2">Standaard 90 dagen.</span>
              </div>
            )}
          </Card>
          <Card className="p-6 space-y-4" data-testid="auto-cleanup-card">
            <div className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              <h2 className="font-heading text-lg font-semibold text-slate-800">Archief automatisch opschonen</h2>
            </div>
            <p className="text-sm text-slate-500">Verwijder gearchiveerde debiteuren, opdrachtgevers, leningen en facturen definitief zodra ze een bepaald aantal dagen in het archief staan. U ontvangt vooraf een waarschuwingsmail met wat er wordt verwijderd. Opdrachtgevers met gekoppelde panden/debiteuren worden overgeslagen.</p>
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <div className="font-medium text-sm text-slate-800">Automatisch opschonen inschakelen</div>
                <div className="text-xs text-red-500">Let op: dit verwijdert gegevens definitief.</div>
              </div>
              <Switch checked={!!catalog.auto_cleanup_enabled} onCheckedChange={(v) => saveCatalog({ ...catalog, auto_cleanup_enabled: v })} data-testid="auto-cleanup-toggle" />
            </div>
            {catalog.auto_cleanup_enabled && (
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-40">
                  <Label className="text-xs text-slate-500">Bewaartermijn (dagen)</Label>
                  <Input type="number" min={1} value={catalog.auto_cleanup_days ?? 365} onChange={(e) => setCatalog((c) => ({ ...c, auto_cleanup_days: e.target.value }))} data-testid="auto-cleanup-days" />
                </div>
                <div className="w-40">
                  <Label className="text-xs text-slate-500">Waarschuwen (dagen vooraf)</Label>
                  <Input type="number" min={0} value={catalog.auto_cleanup_warn_days ?? 14} onChange={(e) => setCatalog((c) => ({ ...c, auto_cleanup_warn_days: e.target.value }))} data-testid="auto-cleanup-warn-days" />
                </div>
                <Button variant="outline" onClick={() => saveCatalog({ ...catalog, auto_cleanup_days: Math.max(1, parseInt(catalog.auto_cleanup_days) || 365), auto_cleanup_warn_days: Math.max(0, parseInt(catalog.auto_cleanup_warn_days) || 14) })} data-testid="auto-cleanup-save"><Save className="h-4 w-4 mr-2" /> Opslaan</Button>
                <span className="text-xs text-slate-400 pb-2">Standaard 365 dagen, 14 dagen vooraf waarschuwen.</span>
              </div>
            )}
          </Card>
          <Card className="p-6 space-y-4" data-testid="catalog-statuses-card">
            <h2 className="font-heading text-lg font-semibold text-slate-800">Debiteurstatussen</h2>
            <p className="text-sm text-slate-500">Beheer de statussen die je aan debiteuren kunt toekennen (bijv. Recidivist, WSNP, Bewind). Nieuwe statussen zijn direct beschikbaar bij het bewerken van een debiteur.</p>
            <div className="flex flex-wrap gap-2">
              {catalog.debtor_statuses.map((s) => (
                <span key={s} className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full pl-3 pr-2 py-1 text-sm" data-testid={`catalog-status-${s}`}>
                  {s}
                  <button onClick={() => removeStat(s)} className="text-blue-400 hover:text-red-600" data-testid={`catalog-status-remove-${s}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </span>
              ))}
              {catalog.debtor_statuses.length === 0 && <span className="text-sm text-slate-400">Nog geen statussen.</span>}
            </div>
            <div className="flex gap-2 max-w-md">
              <Input value={newStat} onChange={(e) => setNewStat(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addStat(); } }} placeholder="Nieuwe status…" data-testid="catalog-new-status-input" />
              <Button variant="outline" onClick={addStat} data-testid="catalog-add-status"><UserPlus className="h-4 w-4 mr-1" /> Toevoegen</Button>
            </div>
          </Card>

          <Card className="p-6 space-y-4" data-testid="catalog-actions-card">
            <h2 className="font-heading text-lg font-semibold text-slate-800">Actietypes (logboek)</h2>
            <p className="text-sm text-slate-500">Vaste acties voor het communicatie-logboek (bijv. LA-brief gestuurd, IB-brief gestuurd, Vroegsignalering, Controle Bewind- en Curateleregister).</p>
            <div className="flex flex-wrap gap-2">
              {(catalog.action_types || []).map((a) => (
                <span key={a} className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full pl-3 pr-2 py-1 text-sm" data-testid={`catalog-action-${a}`}>
                  {a}
                  <button onClick={() => removeAction(a)} className="text-indigo-400 hover:text-red-600" data-testid={`catalog-action-remove-${a}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </span>
              ))}
              {(catalog.action_types || []).length === 0 && <span className="text-sm text-slate-400">Nog geen actietypes.</span>}
            </div>
            <div className="flex gap-2 max-w-md">
              <Input value={newAction} onChange={(e) => setNewAction(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAction(); } }} placeholder="Nieuw actietype…" data-testid="catalog-new-action-input" />
              <Button variant="outline" onClick={addAction} data-testid="catalog-add-action"><UserPlus className="h-4 w-4 mr-1" /> Toevoegen</Button>
            </div>
          </Card>

          <Card className="p-6 space-y-4" data-testid="catalog-profiles-card">
            <h2 className="font-heading text-lg font-semibold text-slate-800">Risicoprofielen</h2>
            <p className="text-sm text-slate-500">Definieer risicoprofielen met een kleur. Je kent er één per debiteur toe.</p>
            <div className="space-y-2">
              {catalog.risk_profiles.map((p) => (
                <div key={p.name} className="flex items-center justify-between border rounded-lg p-2.5" data-testid={`catalog-profile-${p.name}`}>
                  <span className="inline-flex items-center gap-2 text-sm">
                    <span className={`h-3 w-3 rounded-full ${{ green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-red-500", slate: "bg-slate-400" }[p.color] || "bg-slate-400"}`} />
                    {p.name}
                  </span>
                  <button onClick={() => removeProf(p.name)} className="text-slate-400 hover:text-red-600" data-testid={`catalog-profile-remove-${p.name}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              {catalog.risk_profiles.length === 0 && <span className="text-sm text-slate-400">Nog geen profielen.</span>}
            </div>
            <div className="flex gap-2 max-w-lg">
              <Input value={newProf.name} onChange={(e) => setNewProf({ ...newProf, name: e.target.value })} placeholder="Naam profiel…" data-testid="catalog-new-profile-input" />
              <div className="w-36"><Combobox testId="catalog-new-profile-color" value={newProf.color} onChange={(v) => setNewProf({ ...newProf, color: v })} options={[["green", "Groen"], ["amber", "Oranje"], ["red", "Rood"], ["slate", "Grijs"]].map(([k, l]) => ({ value: k, label: l }))} /></div>
              <Button variant="outline" onClick={addProf} data-testid="catalog-add-profile"><UserPlus className="h-4 w-4 mr-1" /> Toevoegen</Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="dropbox" className="space-y-6 mt-4">
          <Card className="p-6 space-y-5" data-testid="dropbox-card">
            <div className="flex items-center gap-2">
              <Cloud className="h-6 w-6 text-blue-600" />
              <h2 className="font-heading text-lg font-semibold text-slate-800">Dropbox back-up</h2>
              {dbx.configured && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Gekoppeld</Badge>}
            </div>
            <p className="text-sm text-slate-500 max-w-2xl">
              Koppel uw eigen Dropbox om dagelijks (en handmatig) alle gegevens én documenten veilig weg te schrijven,
              met een aparte map per opdrachtgever en per debiteur onder <code className="text-xs">/Praktisch-Debiteurenbeheer/</code>.
            </p>

            {dbx.configured ? (
              <div className="space-y-4">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 space-y-1" data-testid="dropbox-status">
                  <div className="flex items-center gap-2 font-medium"><ShieldCheck className="h-4 w-4" /> Verbonden met Dropbox</div>
                  <div>Account: <strong>{dbx.account_name || "—"}</strong>{dbx.account_email ? ` (${dbx.account_email})` : ""}</div>
                  <div>App Key: <span className="font-mono">{dbx.app_key_masked}</span></div>
                  {dbx.last_test_at && <div className="text-xs text-emerald-700">Laatst getest: {fmtDate(dbx.last_test_at)}</div>}
                  <div className="text-xs text-emerald-700">Laatste back-up: {dbx.last_backup_at ? fmtDate(dbx.last_backup_at) : "nog geen"}</div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={backupNow} disabled={!!dbxBusy} className="bg-blue-600 hover:bg-blue-700" data-testid="dropbox-backup-now">
                    <CloudUpload className="h-4 w-4 mr-2" /> {dbxBusy === "backup" ? "Bezig…" : "Maak back-up nu"}
                  </Button>
                  <Button variant="outline" onClick={testDropbox} disabled={!!dbxBusy} data-testid="dropbox-test">
                    <RefreshCw className="h-4 w-4 mr-2" /> {dbxBusy === "test" ? "Testen…" : "Verbinding testen"}
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" disabled={!!dbxBusy} data-testid="dropbox-disconnect"><Unplug className="h-4 w-4 mr-2" /> Ontkoppelen</Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Dropbox ontkoppelen?</AlertDialogTitle>
                        <AlertDialogDescription>De opgeslagen credentials worden verwijderd. Automatische back-ups stoppen totdat u opnieuw koppelt.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuleren</AlertDialogCancel>
                        <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={disconnectDropbox} data-testid="dropbox-disconnect-confirm">Ja, ontkoppelen</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
                <div className="pt-2 border-t">
                  <p className="text-sm text-slate-600 mb-2">Andere Dropbox-app koppelen? Vul hieronder nieuwe gegevens in.</p>
                  <DropboxForm form={dbxForm} setForm={setDbxForm} onSave={saveDropbox} busy={dbxBusy} />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 space-y-2">
                  <div className="font-medium flex items-center gap-2"><Info className="h-4 w-4" /> Waar vind ik deze gegevens?</div>
                  <ol className="list-decimal ml-5 space-y-1 text-blue-800">
                    <li>Ga naar de <a href="https://www.dropbox.com/developers/apps" target="_blank" rel="noreferrer" className="underline font-medium">Dropbox App Console</a> en maak een app (Scoped access, Full Dropbox).</li>
                    <li>Geef de scopes <code className="text-xs">account_info.read</code>, <code className="text-xs">files.content.write</code> en <code className="text-xs">files.metadata.write</code>.</li>
                    <li>Kopieer de <strong>App key</strong> en <strong>App secret</strong> van het tabblad Settings.</li>
                    <li>Genereer eenmalig een <strong>Refresh token</strong> via de OAuth-flow (<code className="text-xs">token_access_type=offline</code>).</li>
                  </ol>
                </div>
                <DropboxForm form={dbxForm} setForm={setDbxForm} onSave={saveDropbox} busy={dbxBusy} />
              </div>
            )}
          </Card>

          {dbx.configured && (
            <Card className="p-6 space-y-4" data-testid="dropbox-history-card">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-base font-semibold text-slate-800 flex items-center gap-2"><History className="h-5 w-5" /> Back-up historie</h3>
                <Button variant="ghost" size="sm" onClick={loadBackups} data-testid="dropbox-history-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Vernieuwen</Button>
              </div>
              {backups.length === 0 ? (
                <p className="text-sm text-slate-400">Nog geen back-ups uitgevoerd. Klik op "Maak back-up nu" om te beginnen.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="dropbox-history-table">
                    <thead>
                      <tr className="text-left text-slate-500 border-b">
                        <th className="py-2 pr-4">Datum &amp; tijd</th>
                        <th className="py-2 pr-4">Type</th>
                        <th className="py-2 pr-4">Status</th>
                        <th className="py-2 pr-4">Bestanden</th>
                        <th className="py-2 pr-4">Opdrachtgevers</th>
                        <th className="py-2 pr-4">Debiteuren</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backups.map((b) => (
                        <tr key={b.id} className="border-b last:border-0" data-testid={`dropbox-backup-row-${b.id}`}>
                          <td className="py-2 pr-4 whitespace-nowrap">{new Date(b.started_at).toLocaleString("nl-NL")}</td>
                          <td className="py-2 pr-4 text-slate-500">{b.trigger}</td>
                          <td className="py-2 pr-4">
                            <Badge className={
                              b.status === "gereed" ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
                              b.status === "running" ? "bg-blue-100 text-blue-700 border-blue-200" :
                              b.status === "gedeeltelijk" ? "bg-amber-100 text-amber-700 border-amber-200" :
                              "bg-red-100 text-red-700 border-red-200"
                            }>
                              {b.status === "running" ? "bezig…" : b.status}
                            </Badge>
                            {b.errors && b.errors.length > 0 && <span className="ml-2 text-xs text-red-500" title={b.errors.join("\n")}>{b.errors.length} fout(en)</span>}
                          </td>
                          <td className="py-2 pr-4 font-medium">{b.files_count}</td>
                          <td className="py-2 pr-4">{b.clients_count}</td>
                          <td className="py-2 pr-4">{b.tenants_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-xs text-slate-400">Automatische back-up draait dagelijks om 02:00. Documenten en gegevens worden per opdrachtgever en debiteur in aparte mappen weggeschreven.</p>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="sms" className="space-y-6 mt-4">
          <Card className="p-6 space-y-5" data-testid="twilio-card">
            <div className="flex items-center gap-2">
              <Smartphone className="h-6 w-6 text-emerald-600" />
              <h2 className="font-heading text-lg font-semibold text-slate-800">SMS-verzending (Twilio)</h2>
              {twilio.configured && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Gekoppeld</Badge>}
            </div>
            <p className="text-sm text-slate-500 max-w-2xl">
              Koppel uw eigen Twilio-account om echte SMS-berichten te versturen bij bulk-aanmaningen en uitbetalingsbevestigingen.
              Zonder koppeling worden SMS-berichten alleen gelogd (mock).
            </p>

            {twilio.configured ? (
              <div className="space-y-4">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 space-y-1" data-testid="twilio-status">
                  <div className="flex items-center gap-2 font-medium"><ShieldCheck className="h-4 w-4" /> Verbonden met Twilio</div>
                  {twilio.friendly_name && <div>Account: <strong>{twilio.friendly_name}</strong></div>}
                  <div>Account SID: <span className="font-mono">{twilio.account_sid_masked}</span></div>
                  <div>Afzendernummer: <span className="font-mono">{twilio.phone_number}</span></div>
                  {twilio.last_test_at && <div className="text-xs text-emerald-700">Laatst getest: {fmtDate(twilio.last_test_at)}</div>}
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="twilio-disconnect"><Unplug className="h-4 w-4 mr-2" /> Ontkoppelen</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Twilio ontkoppelen?</AlertDialogTitle>
                      <AlertDialogDescription>De opgeslagen gegevens worden verwijderd. SMS-berichten vallen daarna terug op mock (alleen loggen).</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuleren</AlertDialogCancel>
                      <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={disconnectTwilio} data-testid="twilio-disconnect-confirm">Ja, ontkoppelen</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <div className="pt-2 border-t">
                  <p className="text-sm text-slate-600 mb-2">Andere gegevens koppelen? Vul hieronder nieuwe waarden in.</p>
                  <TwilioForm form={twForm} setForm={setTwForm} onSave={saveTwilio} busy={twBusy} />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 space-y-2">
                  <div className="font-medium flex items-center gap-2"><Info className="h-4 w-4" /> Waar vind ik deze gegevens?</div>
                  <ol className="list-decimal ml-5 space-y-1 text-emerald-800">
                    <li>Log in op de <a href="https://console.twilio.com" target="_blank" rel="noreferrer" className="underline font-medium">Twilio Console</a>.</li>
                    <li>Kopieer op de dashboard-startpagina de <strong>Account SID</strong> en <strong>Auth Token</strong>.</li>
                    <li>Ga naar <strong>Phone Numbers</strong> en kopieer uw afzendernummer in E.164-formaat (bijv. +31612345678).</li>
                  </ol>
                </div>
                <TwilioForm form={twForm} setForm={setTwForm} onSave={saveTwilio} busy={twBusy} />
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="postvak" className="space-y-6 mt-4">
          <InboxSettings />
        </TabsContent>

        <TabsContent value="modules" className="space-y-6 mt-4">
          <Card className="p-6 space-y-5" data-testid="modules-card">
            <div>
              <h2 className="font-heading text-lg font-semibold text-slate-800">Modules aan/uit</h2>
              <p className="text-sm text-slate-500">Verberg modules die u (nog) niet gebruikt. Zet ze weer aan om ze direct te gebruiken.</p>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-2">Menu-items</div>
              <div className="divide-y border rounded-xl">
                {NAV_MODULES.map((m) => (
                  <div key={m.key} className="flex items-center justify-between px-4 py-3" data-testid={`module-row-${m.key}`}>
                    <span className="text-sm text-slate-700">{m.label}</span>
                    <Switch checked={!hiddenMods.includes(m.key)} onCheckedChange={() => toggleModule(m.key)} data-testid={`module-toggle-${m.key}`} />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-2">Debiteurprofiel — tabbladen</div>
              <div className="divide-y border rounded-xl">
                {TAB_MODULES.map((m) => (
                  <div key={m.key} className="flex items-center justify-between px-4 py-3" data-testid={`module-row-${m.key}`}>
                    <span className="text-sm text-slate-700">{m.label}</span>
                    <Switch checked={!hiddenMods.includes(m.key)} onCheckedChange={() => toggleModule(m.key)} data-testid={`module-toggle-${m.key}`} />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-2">Soorten overeenkomsten <span className="font-normal">— "Overig" is altijd beschikbaar</span></div>
              <div className="divide-y border rounded-xl">
                {KIND_MODULES.map((m) => (
                  <div key={m.key} className="flex items-center justify-between px-4 py-3" data-testid={`module-row-kind-${m.key}`}>
                    <span className="text-sm text-slate-700">{m.label}</span>
                    <Switch checked={!hiddenMods.includes(m.key)} onCheckedChange={() => toggleModule(m.key)} data-testid={`module-toggle-kind-${m.key}`} />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-2">Debiteurprofiel — knoppen</div>
              <div className="divide-y border rounded-xl">
                {BTN_MODULES.map((m) => (
                  <div key={m.key} className="flex items-center justify-between px-4 py-3" data-testid={`module-row-${m.key}`}>
                    <span className="text-sm text-slate-700">{m.label}</span>
                    <Switch checked={!hiddenMods.includes(m.key)} onCheckedChange={() => toggleModule(m.key)} data-testid={`module-toggle-${m.key}`} />
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="branding" className="space-y-6 mt-4">
          <Card className="p-6 space-y-5" data-testid="branding-card">
            <div>
              <h2 className="font-heading text-lg font-semibold text-slate-800">Eigen branding</h2>
              <p className="text-sm text-slate-500">Logo en huisstijlkleur voor brief-PDF's, dossiers en e-mails.</p>
            </div>
            <div>
              <Label>Bedrijfsnaam</Label>
              <Input value={brand.brand_name} onChange={(e) => setBrand((b) => ({ ...b, brand_name: e.target.value }))} placeholder="Praktisch-Debiteurenbeheer" data-testid="brand-name-input" />
            </div>
            <div>
              <Label>Huisstijlkleur</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={brand.brand_color || "#0F1E3D"} onChange={(e) => setBrand((b) => ({ ...b, brand_color: e.target.value }))} className="h-9 w-14 rounded border cursor-pointer" data-testid="brand-color-input" />
                <Input value={brand.brand_color} onChange={(e) => setBrand((b) => ({ ...b, brand_color: e.target.value }))} className="w-32" />
              </div>
            </div>
            <div>
              <Label>Logo</Label>
              <div className="flex items-center gap-4 mt-1">
                {brand.brand_logo ? <img src={brand.brand_logo} alt="logo" className="h-14 rounded border bg-white p-1" data-testid="brand-logo-preview" /> : <div className="h-14 w-14 rounded border bg-slate-50 flex items-center justify-center text-slate-300 text-xs">geen</div>}
                <label className="cursor-pointer text-sm text-blue-600 font-medium" data-testid="brand-logo-upload-label">Logo uploaden<input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="brand-logo-input" /></label>
                {brand.brand_logo && <button className="text-sm text-red-600" onClick={removeLogo} data-testid="brand-logo-remove">Verwijderen</button>}
              </div>
              <p className="text-[11px] text-slate-400 mt-1">PNG/JPG, max 2 MB.</p>
            </div>
            <div className="pt-4 border-t">
              <Label>BCC e-mailadres (kopie van elke verzonden e-mail)</Label>
              <Input value={brand.bcc_email} onChange={(e) => setBrand((b) => ({ ...b, bcc_email: e.target.value }))} placeholder="kopie@voorbeeld.nl (meerdere: komma-gescheiden)" data-testid="brand-bcc-input" />
              <p className="text-[11px] text-slate-400 mt-1">Elke uitgaande e-mail (aanmaningen, dossiers, bevestigingen) wordt automatisch als blinde kopie hierheen gestuurd.</p>
            </div>
            <Button onClick={saveBrand} className="bg-blue-700 hover:bg-blue-800" data-testid="brand-save"><Save className="h-4 w-4 mr-2" /> Opslaan</Button>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TwilioForm({ form, setForm, onSave, busy }) {
  const valid = form.account_sid.trim() && form.auth_token.trim() && form.phone_number.trim();
  return (
    <div className="space-y-3 max-w-xl" data-testid="twilio-form">
      <div>
        <Label>Account SID</Label>
        <Input value={form.account_sid} onChange={(e) => setForm({ ...form, account_sid: e.target.value })} placeholder="ACxxxxxxxxxxxxxxxx" data-testid="twilio-account-sid" />
      </div>
      <div>
        <Label>Auth Token</Label>
        <Input type="password" value={form.auth_token} onChange={(e) => setForm({ ...form, auth_token: e.target.value })} placeholder="Auth token" data-testid="twilio-auth-token" />
      </div>
      <div>
        <Label>Afzendernummer (E.164)</Label>
        <Input value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} placeholder="+31612345678" data-testid="twilio-phone-number" />
      </div>
      <Button onClick={onSave} disabled={!valid || busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="twilio-save">
        <Save className="h-4 w-4 mr-2" /> {busy ? "Verbinden…" : "Opslaan & koppelen"}
      </Button>
    </div>
  );
}

function DropboxForm({ form, setForm, onSave, busy }) {
  const valid = form.app_key.trim() && form.app_secret.trim() && form.refresh_token.trim();
  return (
    <div className="space-y-3 max-w-xl" data-testid="dropbox-form">
      <div>
        <Label>App Key</Label>
        <Input value={form.app_key} onChange={(e) => setForm({ ...form, app_key: e.target.value })} placeholder="bijv. abc123def456" data-testid="dropbox-app-key" />
      </div>
      <div>
        <Label>App Secret</Label>
        <Input type="password" value={form.app_secret} onChange={(e) => setForm({ ...form, app_secret: e.target.value })} placeholder="App secret" data-testid="dropbox-app-secret" />
      </div>
      <div>
        <Label>Refresh Token</Label>
        <Input type="password" value={form.refresh_token} onChange={(e) => setForm({ ...form, refresh_token: e.target.value })} placeholder="Refresh token (offline access)" data-testid="dropbox-refresh-token" />
      </div>
      <Button onClick={onSave} disabled={!valid || !!busy} className="bg-blue-600 hover:bg-blue-700" data-testid="dropbox-save">
        <Save className="h-4 w-4 mr-2" /> {busy === "save" ? "Verbinden…" : "Opslaan & koppelen"}
      </Button>
    </div>
  );
}
